// src/utils/replayPresignupList.ts
// Replays pre-signup crushes from localStorage into the authenticated crush
// vault after first login. Called once per session from claimPrimaryIdsOnce
// (UserData.tsx) after handle conversion (replayPresignupHandles) completes.
//
// Routing:
//   verified === true  → addCrushProducer (same endpoint as handleConfirmCrush).
//                        Produces: contactHash, platform, status:"confirmed",
//                        totalCrushesAdded, dailyCrushesSentTodayTally,
//                        HistoricalAdd, reciprocal detection, analytics.
//   verified === false → updateCrushListVault (pending blob writer).
//                        The Lambda now generates contactHash server-side using
//                        keyMaterial + hmacHash identical to addCrushProducer.
//                        Produces: contactHash, platform, status:"pending".
//
// Ordering: pending entries are written FIRST so that each addCrushProducer
// call (which reads DDB fresh) sees and preserves them via its dedup logic.
//
// Idempotency / imported writeback:
//   - After each successful vault write or addCrushProducer call, the entry is
//     marked imported:true in localStorage immediately. Re-runs skip imported
//     entries and resume at the first un-imported one.
//   - SELF_EDGE is treated as terminal (will never succeed) — marked imported.
//   - DAILY_CAP, NO_VERIFIED_CONTACTS, ACCOUNT_PAUSED, NETWORK_ERROR,
//     ACTIVE_LIST_FULL are retryable — NOT marked imported.
//   - ALREADY_EXISTS / ok:true treated as success — marked imported.
//
// localStorage is cleared only after ALL entries are imported. Booth-state
// clear is gated on the same condition.
//
// TRUST BOUNDARY: authed user context — called with a valid Cognito ID token.

import { updateCrushListVault } from "../components/UserData";

const LS_KEY = "crushhh_presignup_list";

const PHONE_EMAIL_KINDS = new Set(["phone", "email"]);

// Terminal skip reasons: will never succeed on retry.
const TERMINAL_REASONS = new Set(["SELF_EDGE"]);

interface PresignupEntry {
  platform: string;
  contact: string;
  vibe: string;
  addedAt: number;
  verified?: boolean;
  imported?: boolean;
}

interface StoredList {
  version: 1;
  entries: PresignupEntry[];
}

function readStore(): PresignupEntry[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const parsed: StoredList = JSON.parse(raw);
    if (parsed.version !== 1 || !Array.isArray(parsed.entries)) return [];
    return parsed.entries;
  } catch {
    return [];
  }
}

function writeStore(entries: PresignupEntry[]): void {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify({ version: 1, entries }));
  } catch {
    // storage quota full — non-fatal
  }
}

function patchAt(
  entries: PresignupEntry[],
  i: number,
  patch: Partial<PresignupEntry>,
): PresignupEntry[] {
  return entries.map((e, idx) => (idx === i ? { ...e, ...patch } : e));
}

export async function replayPresignupList(
  apiBase: string,
  token: string,
  currentItems: any[],
): Promise<any[]> {
  let live = readStore();

  const unimportedIndices = live
    .map((_, i) => i)
    .filter((i) => !live[i].imported);
  if (unimportedIndices.length === 0) return currentItems;

  const addCrushEndpoint = `${apiBase}add-crush`;
  const skipped: Array<{ contact: string; reason: string }> = [];

  // ---- Step 1: pending (unconfirmed) entries via updateCrushListVault ----
  // Written BEFORE confirmed adds. addCrushProducer reads DDB on every call,
  // so pending entries placed here are visible to (and preserved by) each
  // subsequent addCrushProducer dedup pass.
  const pendingIndices = unimportedIndices.filter((i) => !live[i].verified);
  if (pendingIndices.length > 0) {
    const pendingItems = pendingIndices.map((i) => {
      const e = live[i];
      const kind: "phone" | "email" | "handle" = PHONE_EMAIL_KINDS.has(e.platform)
        ? (e.platform as "phone" | "email")
        : "handle";
      return {
        contact: e.contact,               // Lambda encrypts → contactEnc
        contactType: kind,                // "phone" | "email" | "handle"
        ...(kind === "handle" ? { platform: e.platform } : {}),
        interestLevel: e.vibe as "1" | "2" | "3",
        addedAt: new Date(e.addedAt).toISOString(),
        status: "pending",
        // `verified` intentionally omitted — not a vault field
      };
    });

    // Merge with existing authenticated items to preserve any crushes already
    // in the vault. updateCrushListVault does a full vault SET.
    await updateCrushListVault(apiBase, token, [...currentItems, ...pendingItems]);

    // Vault write succeeded — mark all pending entries imported.
    for (const i of pendingIndices) {
      live = patchAt(live, i, { imported: true });
    }
    writeStore(live);
  }

  // ---- Step 2: confirmed entries through addCrushProducer (serial) ----
  // Serial calls so each sees the updated vault from the previous write.
  const confirmedIndices = unimportedIndices.filter((i) => live[i].verified);
  for (const i of confirmedIndices) {
    const e = live[i];
    const isHandle = !PHONE_EMAIL_KINDS.has(e.platform);
    const payload: Record<string, string> = {
      target: e.contact,
      interest: e.vibe,
    };
    if (isHandle) payload.platform = e.platform;

    try {
      const resp = await fetch(addCrushEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify(payload),
      });
      const json: any = await resp.json().catch(() => ({}));
      if (resp.ok && json?.ok) {
        live = patchAt(live, i, { imported: true });
        writeStore(live);
      } else {
        const reason: string = json?.reason ?? `HTTP_${resp.status}`;
        if (TERMINAL_REASONS.has(reason)) {
          // Will never succeed — mark imported to stop the retry loop.
          live = patchAt(live, i, { imported: true });
          writeStore(live);
        } else {
          // Retryable — leave un-imported.
          skipped.push({ contact: e.contact, reason });
        }
      }
    } catch {
      skipped.push({ contact: e.contact, reason: "NETWORK_ERROR" });
    }
  }

  if (skipped.length > 0) {
    console.warn("[replayPresignupList] skipped entries (will retry next login):", skipped);
  }

  // Clear pre-auth storage only after all entries are imported.
  // Retryable skips leave entries un-imported, so the list persists until
  // those entries resolve (e.g. DAILY_CAP clears the next day).
  if (live.every((e) => e.imported)) {
    localStorage.removeItem(LS_KEY);
    localStorage.removeItem("crushhh_booth_state");
  }

  return currentItems;
}
